import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/boards";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created");
      navigate(next);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-950 text-white">
        <Link to="/" className="font-display text-xl font-black tracking-tighter" data-testid="register-logo">
          KANBN<span className="text-red-500">.</span>
        </Link>
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-6">
            create
          </div>
          <p className="font-display text-4xl font-black tracking-tighter leading-tight max-w-md">
            Set up your first board in under sixty seconds.
          </p>
        </div>
        <div className="font-mono-tight text-[10px] uppercase tracking-widest text-zinc-500">
          v1.0 / 2026
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
            01 / new account
          </div>
          <h2 className="font-display text-4xl font-black tracking-tighter mb-2">Create account.</h2>
          <p className="text-sm text-zinc-500 mb-10">
            Already have one?{" "}
            <Link to="/login" className="text-black underline underline-offset-4" data-testid="register-login-link">
              Sign in
            </Link>
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full border border-zinc-200 px-4 py-3 text-base focus:outline-none focus:border-black transition-colors"
                placeholder="Jane Builder"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-zinc-200 px-4 py-3 text-base focus:outline-none focus:border-black transition-colors"
                placeholder="you@company.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full border border-zinc-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            {error && (
              <div className="border border-red-500 text-red-600 px-4 py-3 text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {loading ? "Creating..." : "Create account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
